import { createElement as _createElement } from "react";
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { formatDMY } from '@infrastructure/helpers';
import VnButton from '@ui/components/VnButton';
import { arrowDown, arrowUp, calendarOutline, funnelOutline, close } from 'ionicons/icons';
import React, { useEffect, useState } from 'react';
import { useTable } from 'react-table';
import DatePicker from 'react-datepicker';
import { IonIcon } from '@ionic/react';
import moment from 'moment';
import Select from 'react-select';
import { gridHeaderDropdownStyle } from '@ui/styles/grid-header-dropdown-style';
import { useDebounce } from '@application/common/hooks/use-debounce';
import { getAllActiveWorkOrderAsync, updateWorkorderToUrgentAsync } from '@application/features/workorders/workorderThunk';
import { WorkorderStatus } from '@application/common/enums/workorder-status';
import { createEmployeeSettingsAsync, getEmployeeSettingsAsync } from '@application/features/auth/authThunks';
import { VisserModule } from '@application/common/enums/visser-module';
const ActiveWorkOrderTable = ({ workOrders, onRowClick, onSorting, onFilter, workorderFilterValues }) => {
    const { workCategoryDropdown, workOrderStatusDropdown, workShopLocationByCurrentEmployeeDropdown } = useAppSelector((state) => state.workorder);
    const { currentUser, currentEmployeeSettings } = useAppSelector((state) => state.auth);
    const dispatch = useAppDispatch();
    const urgentDropdown = [
        {
            label: 'Ja',
            value: true
        },
        {
            label: 'Nee',
            value: false
        }
    ];
    const allowedStatusesforActiveWorkorder = [
        WorkorderStatus.Monteur_Uit_te_voeren,
        WorkorderStatus.Delen_in_bestelling,
        WorkorderStatus.Monteur_Diagnose_stellen
    ];
    const filterWorkorderStatusDropdown = workOrderStatusDropdown?.filter((status) => allowedStatusesforActiveWorkorder.includes(status.value));
    const [sortingCriteria, setSortingCriteria] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [filterValues, setFilterValues] = useState({});
    const columns = React.useMemo(() => [
        { Header: 'Werkorder', accessor: 'workOrderNumber' },
        { Header: 'Aangemaakt', accessor: 'created', Cell: ({ value }) => _jsx(_Fragment, { children: value ? formatDMY(value.toString()) : '' }) },
        { Header: 'Object binnen', accessor: 'objectInside', Cell: ({ value }) => _jsx(_Fragment, { children: value ? formatDMY(value.toString()) : '' }) },
        { Header: 'Klant', accessor: 'debtorName' },
        { Header: 'Omschrijving', accessor: 'description' },
        { Header: 'Werkcategorie', accessor: 'workOrderCategory' },
        { Header: 'Status', accessor: 'workOrderStatus' },
        {
            Header: 'Gewenste leverdatum',
            accessor: 'desiredDeliveryDate',
            Cell: ({ value }) => _jsx(_Fragment, { children: value ? formatDMY(value.toString()) : '' })
        },
        { Header: 'Aangemaakt door', accessor: 'createdBy' },
        { Header: 'Projectleider', accessor: 'projectManagerIdentifier' },
        { Header: 'Info', accessor: 'infoNote' }
    ], []);
    const data = React.useMemo(() => workOrders || [], [workOrders]);
    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({ columns, data });
    const handleRowClick = (row) => {
        onRowClick(row.original.id);
    };
    useEffect(() => {
        dispatch(getEmployeeSettingsAsync(currentUser?.id ? parseInt(currentUser.id) : 0));
        const newSortingCriteria = {
            sortColumnKey: 'created',
            sortDirection: 'desc'
        };
        setSortingCriteria(newSortingCriteria);
    }, []);
    const handleSort = (column) => {
        setSortingCriteria((prevSortingCriteria) => {
            const currentSortDirection = prevSortingCriteria?.sortDirection || 'asc';
            const sortDirection = column.id === prevSortingCriteria?.sortColumnKey ? (currentSortDirection === 'asc' ? 'desc' : 'asc') : 'asc';
            const newSortingCriteria = {
                sortColumnKey: column.id,
                sortDirection: sortDirection
            };
            onSorting(newSortingCriteria);
            return newSortingCriteria;
        });
    };
    const clearFilters = () => {
        const nullFilterValues = Object.fromEntries(Object.keys(filterValues).map((key) => [key, null]));
        const updatedFilters = {
            ...nullFilterValues,
            locationId: filterValues?.locationId ?? -1
        };
        setFilterValues(updatedFilters);
        setShowFilters(!showFilters);
        onFilter(updatedFilters);
    };
    const handleFilterChange = (columnId, value) => {
        setFilterValues((prevFilterValues) => {
            var updatedFilterValues = {
                ...prevFilterValues,
                [columnId]: value
            };
            if (typeof value === 'string' && value.length >= 2) {
                applyFilter(updatedFilterValues);
            }
            else if (value instanceof Date) {
                applyFilter(updatedFilterValues);
            }
            else if (typeof value != 'string' && typeof value != typeof Date) {
                applyFilter(updatedFilterValues);
            }
            if (typeof value === 'string' && value.length == 0) {
                const resetFilterValue = {
                    ...prevFilterValues,
                    [columnId]: null
                };
                updatedFilterValues = resetFilterValue;
                applyFilter(updatedFilterValues);
            }
            return updatedFilterValues;
        });
    };
    const applyFilter = (filterValues) => {
        onFilter(filterValues);
    };
    const [datePickerIsOpen, setDatePickerIsOpen] = useState({
        created: false,
        desiredDeliveryDate: false,
        objectInside: false
    });
    const toggleDatePicker = (columnId) => {
        setDatePickerIsOpen((prevDatePickerIsOpen) => ({
            ...Object.fromEntries(Object.keys(prevDatePickerIsOpen).map((key) => [key, key === columnId ? !prevDatePickerIsOpen[key] : false]))
        }));
    };
    const updateUrgentToggle = (e, id, urgent) => {
        if (id > 0) {
            const urgentDto = {
                id: id,
                Urgent: !urgent
            };
            dispatch(updateWorkorderToUrgentAsync(urgentDto))
                .unwrap()
                .then((res) => {
                dispatch(getAllActiveWorkOrderAsync(workorderFilterValues));
            });
        }
    };
    const updateUrgentToggleDebounce = (e, id, urgent) => {
        debouncedUpdateUrgentToggle(e, id, urgent);
    };
    const debouncedUpdateUrgentToggle = useDebounce(updateUrgentToggle);
    //---------------------- location dropdown
    const [workShopLocationId, setWorkShopLocationId] = useState(-1);
    const handleLocationDropdownChange = (event) => {
        const value = Number(event.target.value);
        if (value !== null && value !== workShopLocationId) {
            setWorkShopLocationId(value);
            dispatch(createEmployeeSettingsAsync({
                employeeId: currentUser?.id ? parseInt(currentUser.id) : 0,
                workshopLocationId: value,
                moduleId: VisserModule.Werkorder
            }))
                .unwrap()
                .then((res) => { })
                .catch((error) => { });
        }
    };
    useEffect(() => {
        if (workShopLocationByCurrentEmployeeDropdown && workShopLocationByCurrentEmployeeDropdown.length > 0) {
            const defaultLocation = workShopLocationByCurrentEmployeeDropdown[0].value;
            if (currentEmployeeSettings) {
                const employeeSavedLocation = currentEmployeeSettings.find((x) => x.moduleId === VisserModule.Werkorder);
                if (employeeSavedLocation) {
                    const locationExists = workShopLocationByCurrentEmployeeDropdown.some((item) => item.value === employeeSavedLocation.workshopLocationId);
                    if (locationExists || employeeSavedLocation.workshopLocationId === 0) {
                        setWorkShopLocationId(employeeSavedLocation.workshopLocationId);
                    }
                    else {
                        setWorkShopLocationId(defaultLocation);
                    }
                }
                else {
                    setWorkShopLocationId(defaultLocation);
                }
            }
            else {
                setWorkShopLocationId(defaultLocation);
            }
        }
    }, [currentEmployeeSettings, workShopLocationByCurrentEmployeeDropdown]);
    useEffect(() => {
        if (workShopLocationId >= 0) {
            setFilterValues((prevFilterValues) => {
                var updatedFilterValues = {
                    ...prevFilterValues,
                    created: null,
                    locationId: workShopLocationId
                };
                applyFilter(updatedFilterValues);
                return updatedFilterValues;
            });
        }
    }, [workShopLocationId]);
    //----------------------
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: 'd-flex justify-content-end mb-2 vn-filter-button-container', children: [_jsxs("div", { style: { marginRight: 10 }, children: [_jsx("label", { className: 'vn-status-label', children: "Werkplaats-Locatie:" }), _jsxs("select", { value: workShopLocationId.toString(), onChange: handleLocationDropdownChange, className: 'vn-select-picker', "aria-label": 'Werkplaats Locatie', children: [workShopLocationByCurrentEmployeeDropdown?.map((item) => (_jsx("option", { value: item.value.toString(), children: item.label }, item.value))), _jsx("option", { value: '0', children: "Alles" }, '0')] })] }), _jsx("div", { className: 'filter-header-container', children: _jsx(VnButton, { style: { borderRadius: '8px', minWidth: '54px', height: '37px', width: '20px' }, customClassName: !showFilters ? 'vn-header-buttons' : 'vn-header-buttons vn-table-filter-remove-button', icon: funnelOutline, customIconClassName: 'vn-action-button-icon', mode: 'danger', onClick: () => {
                                !showFilters ? setShowFilters(!showFilters) : clearFilters();
                            } }) })] }), _jsx("div", { className: 'custom-table-container', children: _jsx("div", { className: 'vn-table-container', children: _jsx("div", { className: 'vn-main', children: _jsxs("table", { ...getTableProps(), className: 'table', children: [_jsx("thead", { className: 'vn-table-thead', children: headerGroups.map((headerGroup) => (_jsxs(React.Fragment, { children: [_jsx("tr", { ...headerGroup.getHeaderGroupProps(), className: 'vn-custom-grid-header-card', children: headerGroup.headers.map((column) => (_jsx("th", { ...column.getHeaderProps(), style: { cursor: 'pointer', padding: '16px' }, children: _jsxs("div", { onClick: () => handleSort(column), className: 'vn-grid-header-sort-inline customTable-header-caption', children: [column.render('Header'), sortingCriteria && sortingCriteria.sortColumnKey === column.id && (_jsx("span", { children: sortingCriteria.sortDirection === 'asc' ? (_jsx(IonIcon, { icon: arrowUp, className: 'ml-1 vn-grid-sorting-icon' })) : (_jsx(IonIcon, { icon: arrowDown, className: 'ml-1 vn-grid-sorting-icon' })) }))] }) }))) }), showFilters && (_jsx("tr", { children: headerGroup.headers.map((column) => (_jsx("td", { style: { borderBottomColor: '#80808047' }, children: column.id === 'desiredDeliveryDate' ||
                                                        column.id === 'created' ||
                                                        column.id === 'objectInside' ? (_jsxs("div", { className: 'input-group', style: { width: '160px' }, children: [_jsx(DatePicker, { portalId: 'root', className: 'form-control vn-Input', open: datePickerIsOpen[column.id], onClickOutside: () => toggleDatePicker(column.id), onChange: (date) => {
                                                                    handleFilterChange(column.id, moment(date).format('YYYY-MM-DD'));
                                                                    toggleDatePicker(column.id);
                                                                }, dateFormat: 'dd/MM/yyyy', timeCaption: 'DateTime', showMonthDropdown: true, showYearDropdown: true, dropdownMode: 'select', value: filterValues[column.id]
                                                                    ? formatDMY(filterValues[column.id].toString())
                                                                    : formatDMY(filterValues[column.id]?.toString()), onCalendarClose: () => toggleDatePicker(column.id), readOnly: true, onKeyDown: (e) => {
                                                                    if (e.key === 'Backspace') {
                                                                        handleFilterChange(column.id, null);
                                                                    }
                                                                } }), filterValues[column.id] && (_jsx("div", { className: 'input-group-append', children: _jsx(IonIcon, { icon: close, className: 'vn-Datepicker-Icon datepicker-cancel-icon', onClick: () => handleFilterChange(column.id, null) }) })), _jsx("div", { className: 'input-group-append', children: _jsx(IonIcon, { icon: calendarOutline, className: 'vn-Datepicker-Icon', onClick: () => toggleDatePicker(column.id) }) })] })) : column.id === 'workOrderStatus' ||
                                                        column.id === 'workOrderCategory' ||
                                                        column.id === 'urgent' ? (_jsx("div", { style: { minHeight: '37px', marginLeft: '-6px' }, children: _jsx(Select, { menuPosition: 'fixed', isSearchable: false, styles: gridHeaderDropdownStyle, placeholder: column.id === 'workOrderCategory'
                                                                ? 'Selecteer categorie'
                                                                : 'Selecteer Status', value: column.id === 'workOrderStatus'
                                                                ? filterWorkorderStatusDropdown?.find((option) => option.value === filterValues[column.id])
                                                                : column.id === 'workOrderCategory'
                                                                    ? workCategoryDropdown?.find((option) => option.value === filterValues[column.id])
                                                                    : urgentDropdown?.find((option) => option.value === filterValues[column.id]), options: column.id === 'workOrderStatus'
                                                                ? filterWorkorderStatusDropdown
                                                                : column.id === 'workOrderCategory'
                                                                    ? workCategoryDropdown
                                                                    : urgentDropdown, onChange: (selectedOption) => {
                                                                if (column.id == 'workOrderStatus') {
                                                                    handleFilterChange('workOrderStatus', selectedOption ? selectedOption.value : undefined);
                                                                }
                                                                else if (column.id == 'workOrderCategory') {
                                                                    handleFilterChange('workOrderCategory', selectedOption ? selectedOption.value : undefined);
                                                                }
                                                                else if (column.id == 'urgent') {
                                                                    handleFilterChange('urgent', selectedOption ? selectedOption.value : undefined);
                                                                }
                                                            }, isClearable: true }) })) : (_jsx("div", { style: { width: '140px' }, children: _jsx("input", { className: 'form-control vn-Input', type: 'text', value: filterValues[column.id] || '', onChange: (e) => handleFilterChange(column.id, e.target.value) }) })) }, column.id ? column.id : 0))) }))] }, headerGroup.id ? headerGroup.id : 0))) }), rows && data && (_jsx("tbody", { ...getTableBodyProps(), className: 'vn-table-tbody', children: rows.map((row) => {
                                        prepareRow(row);
                                        return (_jsx("tr", { ...row.getRowProps(), style: { cursor: 'pointer' }, onClick: () => handleRowClick(row), children: row.cells.map((cell, index) => (_createElement("td", { ...cell.getCellProps(), key: index ? index : 0, style: { padding: '4px' } }, cell.column.id === 'urgent' ? (_jsxs("label", { className: 'Vn-toggle-button-switch', onClick: (e) => {
                                                    e.stopPropagation();
                                                    updateUrgentToggleDebounce(e, row.original.id, cell.value);
                                                }, children: [_jsx("input", { type: 'checkbox', checked: cell.value, disabled: true }, row.original.id), _jsx("span", { className: 'Vn-toggle-button-slider Vn-toggle-button-round' })] })) : (_jsx("span", { children: cell.render('Cell') }))))) }));
                                    }) }))] }) }) }) })] }));
};
export default ActiveWorkOrderTable;
